import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Slider,
  TextField,
  InputAdornment,
  Stack,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
  PyrenzModal,
  PyrenzModalContent,
  PyrenzBlueButtonWithLoading,
} from '@client/theme';
import { themeMakerConfig, FieldConfig, useThemeMakerAPI } from '../../../..';

import CodeMirror from '@uiw/react-codemirror';
import { json as jsonLang } from '@codemirror/lang-json';
import { css as cssLang } from '@codemirror/lang-css';
import { lintGutter } from '@codemirror/lint';
import { oneDark } from '@codemirror/theme-one-dark';

interface ThemeMakerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ThemeMakerModal({ isOpen, onClose }: ThemeMakerModalProps) {
  const {
    modalRef,
    formState,
    submitting,
    handleChange,
    handleCssChange,
    handleSave,
  } = useThemeMakerAPI(onClose);

  const themeJson = useMemo(
    () => JSON.stringify(formState, null, 2),
    [formState]
  );

  const renderField = (sectionId: string, field: FieldConfig, value: any) => {
    switch (field.type) {
      case 'slider':
        return (
          <Stack direction="row" spacing={2} alignItems="center">
            <Slider
              value={value}
              min={field.min}
              max={field.max}
              step={field.step}
              onChange={(_, v) => handleChange(sectionId, field, v as number)}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              type="number"
              value={value}
              onChange={(e) => handleChange(sectionId, field, +e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">px</InputAdornment>
                ),
              }}
              sx={{ width: 90 }}
            />
          </Stack>
        );
      case 'textarea':
        return (
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            value={value}
            onChange={(e) => handleChange(sectionId, field, e.target.value)}
          />
        );
      case 'color':
        return (
          <TextField
            type="color"
            size="small"
            value={value}
            disabled={field.disabled}
            onChange={(e) => handleChange(sectionId, field, e.target.value)}
            sx={{ width: 80, height: 40, p: 0 }}
          />
        );
      case 'number':
        return (
          <TextField
            size="small"
            type="number"
            value={value}
            disabled={field.disabled}
            onChange={(e) => handleChange(sectionId, field, +e.target.value)}
          />
        );
      case 'json':
        return (
          <Box borderRadius={1} overflow="hidden">
            <CodeMirror
              value={JSON.stringify(value, null, 2)}
              height="300px"
              theme={oneDark}
              extensions={[jsonLang(), lintGutter()]}
              onChange={(val) => {
                try {
                  const parsed = JSON.parse(val);
                  handleChange(sectionId, field, parsed);
                } catch {}
              }}
            />
          </Box>
        );
      default:
        return (
          <TextField
            size="small"
            value={value}
            disabled={field.disabled}
            onChange={(e) => handleChange(sectionId, field, e.target.value)}
          />
        );
    }
  };

  return (
    <PyrenzModal open={isOpen} onClose={onClose}>
      <PyrenzModalContent
        ref={modalRef}
        sx={{
          width: '90%',
          maxWidth: 1200,
          height: '85vh',
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
        }}
      >
        <Box flex={1} overflow="auto" pr={1}>
          {themeMakerConfig.map((section) => (
            <Accordion key={section.id} disableGutters square>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>{section.title}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {section.fields.map((field) => {
                    const keys = field.key.split('.');
                    let value = formState[section.id];
                    keys.forEach((k) => {
                      if (value) value = value[k];
                    });
                    return (
                      <Box key={field.key}>
                        <Typography
                          fontSize="0.85rem"
                          fontWeight={500}
                          mb={0.5}
                        >
                          {field.label}
                        </Typography>
                        {renderField(section.id, field, value)}
                      </Box>
                    );
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}

          <Accordion disableGutters square>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600}>Custom CSS</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box borderRadius={1} overflow="hidden">
                <CodeMirror
                  value={formState.css?.raw || ''}
                  height="300px"
                  theme={oneDark}
                  extensions={[cssLang(), lintGutter()]}
                  onChange={(val) => handleCssChange(val)}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Paper
          elevation={3}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            bgcolor: '#1e1e1e',
            color: 'white',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Typography variant="h6" mb={1}>
            Theme JSON Output
          </Typography>
          <Box flex={1} overflow="auto" borderRadius={1}>
            <CodeMirror
              value={themeJson}
              height="100%"
              theme={oneDark}
              extensions={[jsonLang(), lintGutter()]}
              editable={false}
            />
          </Box>

          {formState.css?.raw?.trim() && (
            <>
              <Typography variant="h6" mt={2} mb={1}>
                CSS Preview
              </Typography>
              <Box flex={1} overflow="auto" borderRadius={1}>
                <CodeMirror
                  value={formState.css.raw}
                  height="100%"
                  theme={oneDark}
                  extensions={[cssLang(), lintGutter()]}
                  editable={false}
                />
              </Box>
            </>
          )}

          <Box mt={2} display="flex" justifyContent="center">
            <PyrenzBlueButtonWithLoading
              dataState={submitting ? 'loading' : undefined}
              onClick={handleSave}
            >
              Save Theme
            </PyrenzBlueButtonWithLoading>
          </Box>
        </Paper>
      </PyrenzModalContent>
    </PyrenzModal>
  );
}
